import { Controller } from "@hotwired/stimulus"

// Global command palette: Ctrl+Space or ⌘K / Ctrl+K. Lists every menu entry
// the user may open (filtered as you type, Enter goes there), the contextual
// actions of the current project, and the /search results for records.
export default class extends Controller {
  static targets = ["dialog", "input", "results", "actions", "nav", "navEmpty"]

  connect() {
    this._onKeydown = this._handleKeydown.bind(this)
    document.addEventListener("keydown", this._onKeydown)
    this._onOpenRequest = () => { if (!this.isOpen) this.open() }
    window.addEventListener("command-palette:open", this._onOpenRequest)
    this.timeout = null
    this.selectedIndex = -1
    this._previouslyFocused = null
  }

  disconnect() {
    document.removeEventListener("keydown", this._onKeydown)
    window.removeEventListener("command-palette:open", this._onOpenRequest)
    clearTimeout(this.timeout)
  }

  _handleKeydown(event) {
    const ctrlSpace = event.ctrlKey && !event.metaKey && !event.altKey && (event.code === "Space" || event.key === " ")
    const cmdK = (event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k"
    if (ctrlSpace || cmdK) {
      event.preventDefault()
      this.isOpen ? this.close() : this.open()
    } else if (event.key === "Escape" && this.isOpen) {
      event.preventDefault()
      this.close()
    } else if (event.key === "Tab" && this.isOpen) {
      // Trap focus inside the modal: results are reached with the arrow keys, so
      // keeping focus on the input means Tab can never escape to the page behind.
      event.preventDefault()
      this.inputTarget.focus()
    }
  }

  get isOpen() {
    return !this.dialogTarget.classList.contains("hidden")
  }

  open() {
    this._previouslyFocused = document.activeElement
    this.dialogTarget.classList.remove("hidden")
    this.inputTarget.value = ""
    this.selectedIndex = -1
    this._clearResults()
    this._clearHighlight()
    this._filterActions("")
    this._filterNav("")
    this.inputTarget.focus()
  }

  close() {
    this.dialogTarget.classList.add("hidden")
    this._clearResults()
    this._clearHighlight()
    if (this._previouslyFocused && this._previouslyFocused.focus) this._previouslyFocused.focus()
  }

  closeFromBackdrop(event) {
    if (event.target === this.dialogTarget) this.close()
  }

  search() {
    clearTimeout(this.timeout)
    const query = this.inputTarget.value.trim()
    this.selectedIndex = -1
    this._clearHighlight()
    this._filterActions(query.toLowerCase())
    this._filterNav(query.toLowerCase())
    if (query.length < 2) {
      this._clearResults()
      return
    }
    this.timeout = setTimeout(() => this._fetch(query), 200)
  }

  async _fetch(query) {
    const response = await fetch(`/search?q=${encodeURIComponent(query)}`, {
      headers: { Accept: "text/html", "X-Requested-With": "XMLHttpRequest" }
    })
    if (!response.ok) return
    this.resultsTarget.innerHTML = await response.text()
  }

  _clearResults() {
    this.resultsTarget.innerHTML = ""
  }

  _filterActions(query) {
    if (!this.hasActionsTarget) return
    this.actionsTarget.querySelectorAll(".command-action").forEach((el) => {
      const label = el.dataset.commandPaletteActionLabel || ""
      el.classList.toggle("hidden", query.length > 0 && !label.includes(query))
    })
    const anyVisible = Array.from(this.actionsTarget.querySelectorAll(".command-action")).some((el) => !el.classList.contains("hidden"))
    this.actionsTarget.classList.toggle("hidden", !anyVisible)
  }

  // Menu entries match on every word typed, in any order ("spese fin" → Spese · Finanza).
  _filterNav(query) {
    if (!this.hasNavTarget) return
    const words = query.split(/\s+/).filter(Boolean)
    let visible = 0
    this.navTarget.querySelectorAll(".command-nav").forEach((el) => {
      const label = el.dataset.commandPaletteNavLabel || ""
      const match = words.every((w) => label.includes(w))
      el.classList.toggle("hidden", !match)
      if (match) visible += 1
    })
    if (this.hasNavEmptyTarget) this.navEmptyTarget.classList.toggle("hidden", visible > 0 || words.length === 0)
  }

  // bg-primary/10 is a defined token (bg-hover has no backing color, renders nothing).
  // Clear from every candidate row, not just the currently-visible _items, so a
  // highlight left on an action that later gets filtered out doesn't linger.
  _clearHighlight() {
    if (this.hasActionsTarget) this.actionsTarget.querySelectorAll(".command-action").forEach((el) => el.classList.remove("bg-primary/10"))
    if (this.hasNavTarget) this.navTarget.querySelectorAll(".command-nav").forEach((el) => el.classList.remove("bg-primary/10"))
    this.resultsTarget.querySelectorAll(".search-result-item").forEach((el) => el.classList.remove("bg-primary/10"))
  }

  get _items() {
    return [
      ...(this.hasActionsTarget ? this.actionsTarget.querySelectorAll(".command-action:not(.hidden)") : []),
      ...(this.hasNavTarget ? this.navTarget.querySelectorAll(".command-nav:not(.hidden)") : []),
      ...this.resultsTarget.querySelectorAll(".search-result-item")
    ]
  }

  navigate(event) {
    if (event.key !== "ArrowDown" && event.key !== "ArrowUp" && event.key !== "Enter") return
    const items = this._items
    if (items.length === 0) return
    event.preventDefault()

    if (event.key === "Enter") {
      // No explicit choice yet: Enter takes the first match, so "spe⏎" is enough.
      const target = items[this.selectedIndex < 0 ? 0 : this.selectedIndex]
      if (target) target.click()
      return
    }
    const delta = event.key === "ArrowDown" ? 1 : -1
    if (this.selectedIndex === -1) {
      this.selectedIndex = delta === 1 ? 0 : items.length - 1
    } else {
      this.selectedIndex = (this.selectedIndex + delta + items.length) % items.length
    }
    this._clearHighlight()
    items[this.selectedIndex].classList.add("bg-primary/10")
    items[this.selectedIndex].scrollIntoView({ block: "nearest" })
  }
}
